import React, { useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { ErrorBoundary } from 'wsm-error-boundary';
import { useFlags, useLabels } from 'wsm-common-data';
import { CommonModal } from 'wsm-common-modal';

import Options from '../components/Options';
import Packages from '../components/Packages';
import GridStylePackages from '../components/restyle/GridStylePackages';
import ListStylePackages from '../components/restyle/ListStylePackages';
import Mathbox from '../components/Mathbox';
import DealerAccessories from '../components/DealerAccessories';

const Widget = ({
	deviceType,
	packages,
	options,
	accessories,
	showPricing,
	locale,
	prefs,
	widgetInfo
}) => {
	let optionsBorder;
	let displayComponent;

	const flags = useFlags();
	const labels = useLabels();
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [isListClassReady, setIsListClassReady] = useState(false);
	const isGridStylePackages = flags['enable-restyle-ws-packages-options'];
	const isListLayout =
		isGridStylePackages && prefs?.packagesOptionsLayout === 'list';
	const togglePackageDetails = prefs?.togglePackageDetails === 'true';
	const isEnableDisclaimer = prefs?.enableDisclaimers === 'true';
	const modalTitle = prefs?.widgetHeading
		? labels.get(prefs.widgetHeading)
		: '';

	useLayoutEffect(() => {
		const widgetName = widgetInfo?.widgetName || 'ws-packages-options';
		const appRoot = document.querySelector(
			`[data-widget-name="${widgetName}"]`
		);
		const containerRoot =
			appRoot?.closest('.page-section') || appRoot?.parentElement;

		if (containerRoot) {
			containerRoot.classList.add('packages-options-container');
		}

		if (isListLayout && appRoot) {
			appRoot.classList.add('ws-packages-options-list');
		}

		setIsListClassReady(true);

		return () => {
			if (containerRoot) {
				containerRoot.classList.remove('packages-options-container');
			}
			if (appRoot) {
				appRoot.classList.remove('ws-packages-options-list');
			}
		};
	}, [isListLayout, widgetInfo?.widgetName]);

	if (isListLayout && !isListClassReady) {
		return null;
	}

	const onOpenModal = () => {
		setIsOpenModal(true);
	};

	if (
		packages.length === 0 &&
		options.length === 0 &&
		accessories.length === 0
	) {
		return null;
	}

	if (packages.length > 0 && options.length > 0) {
		optionsBorder = <div key={Math.random()} className="my-4 border-top" />;
	}

	if (prefs.useMathBox === 'true' && options.length > 0) {
		displayComponent = <Mathbox options={options} locale={locale} />;
	} else {
		const modalTitleHTML = (
			<span dangerouslySetInnerHTML={{ __html: modalTitle }} />
		);

		const gridItemProps = {
			accessories,
			packages,
			options,
			widgetInfo,
			locale,
			onOpenModal,
			isEnableDisclaimer
		};
		let widgetContent;

		if (isListLayout) {
			widgetContent = (
				<>
					<ListStylePackages
						packages={packages}
						options={options}
						accessories={accessories}
						locale={locale}
						onOpenModal={onOpenModal}
						togglePackageDetails={togglePackageDetails}
						prefs={prefs}
					/>
					<CommonModal
						title={modalTitleHTML}
						isOpen={isOpenModal}
						width="responsive"
						onClose={() => setIsOpenModal(false)}
						id="options-modal"
					>
						<ListStylePackages
							packages={packages}
							options={options}
							accessories={accessories}
							locale={locale}
							onOpenModal={onOpenModal}
							togglePackageDetails={togglePackageDetails}
							prefs={prefs}
							widgetInfo={widgetInfo}
							isModal
						/>
					</CommonModal>
				</>
			);
		} else if (isGridStylePackages) {
			widgetContent = (
				<>
					<GridStylePackages {...gridItemProps} />
					<CommonModal
						title={modalTitleHTML}
						isOpen={isOpenModal}
						width="responsive"
						onClose={() => setIsOpenModal(false)}
						id="options-modal"
					>
						<GridStylePackages {...gridItemProps} isModal />
					</CommonModal>
				</>
			);
		} else {
			widgetContent = (
				<>
					<Packages
						deviceType={deviceType}
						packages={packages}
						widgetInfo={widgetInfo}
						showPricing={showPricing}
						locale={locale}
					/>
					{optionsBorder}
					<Options
						deviceType={deviceType}
						options={options}
						showPricing={showPricing}
						locale={locale}
						widgetInfo={widgetInfo}
					/>
					{prefs.displayDealerAccessories === 'true' &&
						accessories.length > 0 && [
							optionsBorder,
							<DealerAccessories
								deviceType={deviceType}
								accessories={accessories}
								showPricing={showPricing}
								locale={locale}
								widgetInfo={widgetInfo}
							/>
						]}
				</>
			);
		}

		displayComponent = (
			<ErrorBoundary newRelicPageAction="WS PACKAGES OPTIONS ERROR BOUNDARY">
				{widgetContent}
			</ErrorBoundary>
		);
	}

	return displayComponent;
};

Widget.propTypes = {
	deviceType: PropTypes.string,
	locale: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	packages: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired,
			subitems: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
		})
	),
	accessories: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	showPricing: PropTypes.bool.isRequired,
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string,
		widgetName: PropTypes.string
	}),
	prefs: PropTypes.shape({
		useMathBox: PropTypes.string.isRequired,
		displayDealerAccessories: PropTypes.string.isRequired,
		widgetHeading: PropTypes.string.isRequired,
		enableDisclaimers: PropTypes.string,
		packagesOptionsLayout: PropTypes.string,
		togglePackageDetails: PropTypes.string
	})
};

Widget.defaultProps = {
	deviceType: 'DESKTOP',
	locale: 'en_US',
	options: [],
	packages: [],
	accessories: [],
	widgetInfo: {
		windowId: 'packages-options1',
		widgetName: 'ws-packages-options'
	}
};

const mapStateToProps = (state) => {
	return {
		deviceType: state.requestData.deviceType,
		locale: state.requestData.locale,
		options: state.options,
		packages: state.packages,
		accessories: state.accessories,
		showPricing: state.settings.showPricing,
		widgetInfo: {
			windowId: state.requestData.windowId,
			widgetName: state.requestData.widgetName
		},
		prefs: state.prefs
	};
};

const mapDispatchToProps = (_dispatch) => ({});

const WidgetContainer = connect(mapStateToProps, mapDispatchToProps)(Widget);

export default WidgetContainer;
